import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'
import type { TicketBrief } from './easeedesk'

/**
 * Contractul change management-ului (v4/api, App\Easeedesk\Change\*): cererea
 * de schimbare de pe fisa tichetului, voturile CAB, legatura incident -> change
 * si setarile din Settings → Easeedesk → Change management.
 *
 * Numele campurilor sunt exact cele pe care le scrie serverul.
 */

export type ApprovalState = 'draft' | 'pending' | 'approved' | 'rejected'
export type CabRule = 'all' | 'majority' | 'any'
export type Vote = 'approve' | 'reject' | 'comment'

export interface Pair<V = number> {
  value: V
  label: string
}

export interface ChangeCi {
  id: number
  name: string
  class_id: number
}

export interface RiskQuestion {
  ID: number
  question: string
  options: { label: string; score: number }[]
  sort_order: number
}

export interface CabVote {
  ID: number
  round: number
  user_id: number
  user_name: string
  vote: Vote
  comment: string
  created_at: string
}

export interface ChangeClash {
  change: number
  kind: 'change' | 'window'
  id: number
  name: string
  reason: string
}

export interface ChangeRecord {
  change_class: number
  class_label: string
  implementation_plan: string
  backout_plan: string
  planned_start: string
  planned_end: string
  risk_level: number
  risk_label: string
  risk_score: number | null
  risk_source: string
  approval_state: ApprovalState
  approval_round: number
  requested_at: string
  decided_at: string
  cab: { group_id: number; group_name: string; rule: CabRule; rule_label: string } | null
}

export interface Tally {
  state: ApprovalState
  approvals: number
  rejections: number
  waiting: number
  members: number
}

export type TicketChange =
  | { ticket: number; isChange: false }
  | {
      ticket: number
      isChange: true
      closed: boolean
      classes: Pair[]
      risks: Pair[]
      canEdit: boolean
      change: ChangeRecord
      cis: ChangeCi[]
      questions: RiskQuestion[]
      answers: Record<string, { answer: string; score: number }>
      thresholds: { medium_from: number; high_from: number }
      votes: CabVote[]
      tally: Tally | null
      isMember: boolean
      myVote: Vote | null
      approvalStep: { id: number; name: string; to: string } | null
      clashes: ChangeClash[]
      causedIncidents: TicketBrief[]
      /** Sedintele CAB care au change-ul pe agenda (lipsesc la un server mai vechi). */
      meetings?: ChangeMeeting[]
      /** Ciorna biroului AI din care s-a nascut change-ul (null = deschis de un om). */
      desk?: ChangeDeskDraft | null
    }

export interface ChangeDeskDraft {
  draft_id: number
  run_id: number
  ticket_id: number
  requester_email: string
  consent_state: string
  draft_status: string
}

export interface ChangeMeeting {
  ID: number
  title: string
  meeting_at: string
  meeting_at_local: string
  status: 'planned' | 'held' | 'cancelled'
  decision: 'approve' | 'reject' | 'defer' | ''
}

export interface ChangeAnswer {
  status?: string
  message: string
  state?: ApprovalState
  reset?: boolean
}

export function ticketChangeQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'change'],
    queryFn: () => apiGet<TicketChange>(`/easeedesk/tickets/${id}/change`),
  })
}

export interface ChangeForm {
  change_class: number
  implementation_plan: string
  backout_plan: string
  planned_start: string
  planned_end: string
  risk_level: number
}

export function saveTicketChange(id: number, form: ChangeForm) {
  return apiPost<ChangeAnswer>(`/easeedesk/tickets/${id}/change`, { ...form })
}

export function saveAssessment(id: number, answers: Record<string, number>) {
  return apiPostJson<ChangeAnswer & { score?: number; risk_level?: number }>(`/easeedesk/tickets/${id}/change/assessment`, {
    answers,
  })
}

export function requestApproval(id: number) {
  return apiPost<ChangeAnswer>(`/easeedesk/tickets/${id}/change/request_approval`, {})
}

export function castVote(id: number, vote: Vote, comment: string) {
  return apiPost<ChangeAnswer>(`/easeedesk/tickets/${id}/change/vote`, { vote, comment })
}

export function linkCausedBy(incident: number, change: number) {
  return apiPost<ChangeAnswer>(`/easeedesk/tickets/${incident}/caused_by`, { change })
}

export function unlinkCausedBy(incident: number, change: number) {
  return apiPost<ChangeAnswer>(`/easeedesk/tickets/${incident}/caused_by/${change}/remove`, {})
}

/* ------------------------------------------------------------------ */
/* Setarile                                                            */
/* ------------------------------------------------------------------ */

const SETTINGS = '/settings/easeedesk/change_management'

export interface CabGroup {
  ID: number
  name: string
  description: string
  members: { ID: number; name: string }[]
}

export interface ChangeSettingsPage {
  title: string
  moduleUrl: string
  groups: CabGroup[]
  staff: { ID: number; name: string }[]
  questions: RiskQuestion[]
  thresholds: { medium_from: number; high_from: number }
  rules: Pair<CabRule>[]
  classes: Pair[]
}

export function changeSettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'change_management'],
    queryFn: () => apiGet<ChangeSettingsPage>(SETTINGS),
  })
}

export function changeTypesQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'change_management', 'types'],
    queryFn: () => apiGet<{ types: Record<string, number>; classes: Pair[] }>(`${SETTINGS}/types`),
  })
}

export function markChangeType(type: number, changeClass: number) {
  return apiPost<{ ok?: boolean; message: string }>(`${SETTINGS}/types/${type}`, { change_class: changeClass })
}

export function createChangeTemplate(name: string) {
  return apiPost<{ ok?: boolean; ID?: number; message: string }>(`${SETTINGS}/template`, { name })
}

export function saveCabGroup(id: number, group: { name: string; description: string; members: number[] }) {
  return apiPostJson<{ ok?: boolean; ID?: number; message: string }>(
    id > 0 ? `${SETTINGS}/groups/${id}` : `${SETTINGS}/groups`,
    group,
  )
}

export function removeCabGroup(id: number) {
  return apiPost<{ ok?: boolean; message: string }>(`${SETTINGS}/groups/${id}/remove`, {})
}

export function saveRiskQuestion(
  id: number,
  question: { question: string; options: { label: string; score: number }[]; sort_order: number },
) {
  return apiPostJson<{ ok?: boolean; ID?: number; message: string }>(
    id > 0 ? `${SETTINGS}/questions/${id}` : `${SETTINGS}/questions`,
    question,
  )
}

export function removeRiskQuestion(id: number) {
  return apiPost<{ ok?: boolean; message: string }>(`${SETTINGS}/questions/${id}/remove`, {})
}

export function saveRiskThresholds(mediumFrom: number, highFrom: number) {
  return apiPostJson<{ ok?: boolean; message: string }>(`${SETTINGS}/thresholds`, {
    medium_from: mediumFrom,
    high_from: highFrom,
  })
}

export interface StepCab {
  cab_group_id: number
  cab_rule: CabRule
  groups: { ID: number; name: string; members: number }[]
  rules: Pair<CabRule>[]
}

export function stepCabQuery(step: number, enabled = true) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'change_management', 'step_cab', step],
    queryFn: () => apiGet<StepCab>(`${SETTINGS}/steps/${step}/cab`),
    enabled,
  })
}

export function saveStepCab(step: number, groupId: number, rule: CabRule) {
  return apiPostJson<{ ok?: boolean }>(`${SETTINGS}/steps/${step}/cab`, { cab_group_id: groupId, cab_rule: rule })
}

export function workflowCabQuery(workflow: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'change_management', 'workflow_cab', workflow],
    queryFn: () =>
      apiGet<{
        steps: Record<string, { cab_group_id: number; cab_rule: CabRule; group_name: string; rule_label: string }>
      }>(`${SETTINGS}/workflows/${workflow}/cab`),
    enabled: workflow > 0,
  })
}

/** Culoarea starii aprobarii - aceeasi pe fisa si in calendar. */
export const APPROVAL_COLOR: Record<ApprovalState, string> = {
  draft: '#8c97a1',
  pending: '#f0ad4e',
  approved: '#3c9a5f',
  rejected: '#c9302c',
}

export const APPROVAL_LABEL: Record<ApprovalState, string> = {
  draft: 'Draft',
  pending: 'Waiting for the CAB',
  approved: 'Approved',
  rejected: 'Rejected',
}
